import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TrophyIcon from '@mui/icons-material/EmojiEventsOutlined';
import EnvoiIcon from '@mui/icons-material/CloudUploadOutlined';
import EnvoyeIcon from '@mui/icons-material/CloudDoneOutlined';
import LienIcon from '@mui/icons-material/OpenInNew';
import LeverIcon from '@mui/icons-material/AlarmOutlined';
import { Link } from 'react-router-dom';

import { libelleOrigine, type SeancePlanifiee } from './planningApi.ts';
import { type ZoneRow } from '../Construction/allureTable.ts';
import { ZoneChip } from '../Allures/couleursZones.tsx';
import { formaterDuree, formaterDureeCourse } from '../Construction/formatDuree.ts';
import ProfilSeance from '../Construction/ProfilSeance.tsx';
import { normaliserAllures } from '../Construction/seanceEngine.ts';
import { formaterKm } from './dates.ts';
import { ecartUA } from './charge.ts';

function Mesure({ libelle, valeur }: { libelle: string; valeur: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{libelle}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', fontVariantNumeric: 'tabular-nums' }}>{valeur}</Typography>
    </Box>
  );
}

const code: React.CSSProperties = { fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-word' };

/**
 * Detail d'une seance planifiee (clic dans le calendrier) : mesures,
 * visualisation, composition avec le modele, notes, et actions.
 */
export default function DetailSeancePlanifiee({
  p,
  vma,
  table,
  libelleType,
  chargeCorpsUA,
  ajuster,
  onFermer,
  onEnvoyer,
  onRetirer,
  onDecompte,
  onActualiser,
}: {
  p: SeancePlanifiee | null;
  vma: number;
  table: ZoneRow[];
  libelleType?: string | null;
  // UA du corps de seance seul (seance posee avec un modele), pour comparer.
  chargeCorpsUA?: number | null;
  // Notation affichee avec l'ajustement des recuperations (preference).
  ajuster: (notation: string) => string;
  onFermer: () => void;
  // Actions facultatives (ex : pas de retrait depuis la page d'accueil).
  onEnvoyer?: (p: SeancePlanifiee) => void;
  onRetirer?: (p: SeancePlanifiee) => void;
  // Competition : pose un decompte de semaines jusqu'a sa semaine.
  onDecompte?: (p: SeancePlanifiee) => void;
  // Origine modifiee depuis la pose : remet la seance posee a jour.
  onActualiser?: (p: SeancePlanifiee) => void;
}) {
  if (!p) return <Dialog open={false} onClose={onFermer} />;
  const competition = p.categorie === 'COMPETITION';
  // Sans date : apercu d'une seance de la liste, pas encore posee.
  const dateLongue = !p.date ? null : new Date(`${p.date}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const corps = ajuster(p.notationCorps ?? p.notation);
  const echauffement = p.notationEchauffement ? ajuster(p.notationEchauffement) : null;
  const recuperation = p.notationRecuperation ? ajuster(p.notationRecuperation) : null;
  const duree = p.dureeTotaleS ? (competition ? formaterDureeCourse(p.dureeTotaleS) : formaterDuree(p.dureeTotaleS)) : '—';

  return (
    <Dialog open onClose={onFermer} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {competition && <TrophyIcon />}
          {normaliserAllures(p.nom)}
        </Box>
        {dateLongue ? (
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{dateLongue}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">Pas encore planifiée : glisse-la sur un jour du calendrier</Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {p.etatOrigine && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={p.etatOrigine === 'MODIFIEE' && onActualiser ? (
              <Button color="inherit" size="small" onClick={() => onActualiser(p)}>Mettre à jour</Button>
            ) : undefined}
          >
            {libelleOrigine(p.etatOrigine)}{' '}
            {p.etatOrigine === 'MODIFIEE'
              ? 'Cette séance posée est restée telle quelle.'
              : 'Cette séance posée reste valable telle quelle ; retire-la et pose une autre séance si besoin.'}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          {competition && <Chip size="small" color="warning" variant="outlined" label="Compétition" />}
          {libelleType && <Chip size="small" variant="outlined" label={libelleType} />}
          {p.zonePrincipale && <ZoneChip nom={p.zonePrincipale} table={table} />}
          {p.dateEnvoiIcu && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<EnvoyeIcon />}
              label={`Envoyée vers intervals.icu le ${new Date(p.dateEnvoiIcu).toLocaleDateString('fr-FR')}`}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
          <Mesure libelle="Durée" valeur={duree} />
          <Mesure libelle="Distance" valeur={formaterKm(p.distanceTotaleM) || '—'} />
          <Mesure libelle="Charge" valeur={p.chargeUA ? `${Math.round(p.chargeUA)} UA` : '—'} />
          {p.chargeUA != null && chargeCorpsUA != null && (
            <Mesure
              libelle="Corps seul / écart modèle"
              valeur={`${chargeCorpsUA} UA (${ecartUA(p.chargeUA, chargeCorpsUA)})`}
            />
          )}
          <Mesure libelle="RPE" valeur={p.rpe ? `${p.rpe}/10` : '—'} />
        </Box>

        <ProfilSeance notations={{ echauffement, corps, recuperation }} vma={vma} table={table} hauteur={160} />

        <Divider sx={{ my: 2 }} />

        {p.nomModele ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.75, alignItems: 'baseline' }}>
            <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              {p.date ? 'Composée' : 'Sera composée'} avec le modèle « {p.nomModele} »
            </Typography>
            {echauffement && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Échauffement</Typography>
                <span style={code}>{echauffement}</span>
              </>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Corps</Typography>
            <span style={code}>{corps}</span>
            {recuperation && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Récupération</Typography>
                <span style={code}>{recuperation}</span>
              </>
            )}
          </Box>
        ) : (
          <span style={code}>{ajuster(p.notation)}</span>
        )}

        {p.notes && (
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', whiteSpace: 'pre-line' }}>{p.notes}</Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          {p.lienCourse && (
            <Button size="small" component="a" href={p.lienCourse} target="_blank" rel="noopener noreferrer" endIcon={<LienIcon />}>
              Site de la course
            </Button>
          )}
          {p.idSeance && p.etatOrigine !== 'SUPPRIMEE' && p.etatOrigine !== 'INACCESSIBLE' && (
            <Button size="small" component={Link} to={`/seances/${p.idSeance}`}>
              Ouvrir la séance d'origine
            </Button>
          )}
          {p.date && p.dureeTotaleS && (
            <Button
              size="small"
              component={Link}
              to={`/lever?date=${p.date}&seance=${encodeURIComponent(p.id)}&duree=${Math.round(p.dureeTotaleS / 60)}`}
              startIcon={<LeverIcon />}
            >
              Heure de lever
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {onRetirer && <Button color="error" onClick={() => onRetirer(p)}>Retirer du calendrier</Button>}
        <Box sx={{ flex: 1 }} />
        {onDecompte && p.categorie === 'COMPETITION' && (
          <Button onClick={() => onDecompte(p)}>Décompte jusqu'à cette course</Button>
        )}
        {onEnvoyer && (
          <Button startIcon={<EnvoiIcon />} onClick={() => onEnvoyer(p)}>
            {p.dateEnvoiIcu ? 'Renvoyer vers intervals.icu' : 'Envoyer vers intervals.icu'}
          </Button>
        )}
        <Button variant="contained" onClick={onFermer}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
